"use client";

import React from 'react';
import { useGameQuality } from '@/app/providers/GameQualityProvider';
import { motion } from 'framer-motion';
import { yawStore } from '@/app/hooks/useYawStore';

const Compass: React.FC = () => {
    const [continuousRotation, setContinuousRotation] = React.useState(0);
    const prevYawRef = React.useRef<number | null>(null);

    // Subscribe to yaw changes from the store
    React.useEffect(() => {
        const unsubscribe = yawStore.subscribe((yaw) => {
            // Fix coordinate system alignment: PhotoSphere yaw=0 faces +Z (south), but compass 0° should be north
            // Add π (180°) offset to align photosphere center (north) with compass north
            const correctedYaw = yaw + (Math.PI / 2);
            // Negate for compass display (clockwise rotation)
            const negatedYaw = -correctedYaw;
            const currentDegrees = (negatedYaw * 180) / Math.PI;
            
            if (prevYawRef.current !== null) {
                const prevDegrees = (prevYawRef.current * 180) / Math.PI;
                let deltaRotation = currentDegrees - prevDegrees;

                // Handle wraparound by choosing the shorter rotation path
                if (deltaRotation > 180) {
                    deltaRotation -= 360;
                } else if (deltaRotation < -180) {
                    deltaRotation += 360;
                }

                setContinuousRotation(prev => prev + deltaRotation);
            } else {
                // Initialize with current degrees
                setContinuousRotation(currentDegrees);
            }

            prevYawRef.current = negatedYaw;
        });

        return unsubscribe;
    }, []);

    // Get backdrop blur intensity from game quality settings
    const { getBackdropBlurIntensity } = useGameQuality();

    // Create cardinal points for linear display
    const cardinals = [
        { label: 'N', angle: 0 },
        { label: 'NE', angle: 45 },
        { label: 'E', angle: 90 },
        { label: 'SE', angle: 135 },
        { label: 'S', angle: 180 },
        { label: 'SW', angle: 225 },
        { label: 'W', angle: 270 },
        { label: 'NW', angle: 315 },
    ];

    // Calculate translation offset based on continuous rotation
    // Each degree moves the compass 3px to create smooth movement with more spacing
    const translateX = -continuousRotation * 3;

    // Get normalized degrees for display (0-360)
    //const normalizedDegrees = ((continuousRotation % 360) + 360) % 360;

    const totalSets = 20; // Number of repeated sets of cardinal directions

    return (
        <motion.div
            initial={{ opacity: 0, y: -20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -20, x: "-50%" }}
            transition={{ duration: 0.3 }}
            className={`fixed top-16 md:top-9 xl:top-6 left-1/2 z-[30] pointer-events-none select-none`}
        >
            {/* Compass container */}
            <div className="relative w-80 xl:w-96 h-6 xl:h-10">
                {/* Background */}
                <div
                    className="absolute inset-0 rounded-full overflow-hidden"
                >
                    {/* Black background */}
                    <div className={`absolute bg-black/60 ${getBackdropBlurIntensity('sm')} w-full h-full`}></div>

                    {/* Fixed center indicator line */}
                    <div className="absolute left-1/2 top-0 translate-x-[-3px] w-[2px] h-[6px] bg-white/50 rounded-b-full" />
                    <div className="absolute left-1/2 bottom-0 translate-x-[-3px] w-[2px] h-[6px] bg-white/50 rounded-t-full" />

                    {/* Moving compass strip */}
                    <div
                        className="w-full h-full"
                        style={{ maskImage: 'linear-gradient(to right, transparent, black 40%, black 60%, transparent)' }}
                    >
                        <div
                            className="absolute top-0 h-full flex items-center whitespace-nowrap ease-out"
                            style={{
                                transform: `translateX(${translateX}px)`,
                                transition: 'transform 0.1s ease-out',
                            }}
                        >
                            {/* Render multiple sets of cardinal directions for smooth wraparound */}
                            {Array.from({ length: totalSets }, (_, setIndex) => (
                                <React.Fragment key={setIndex}>
                                    {cardinals.map((cardinal, index) => {
                                        const relativeSetIndex = setIndex - (totalSets / 2);
                                        const totalAngle = cardinal.angle + (relativeSetIndex * 360);
                                        const position = (totalAngle * 3) + 190; // Centered for 3px per degree (was 285 for 2px)

                                        return (
                                            <React.Fragment key={`${setIndex}-${index}`}>
                                                {/* Cardinal direction */}
                                                <div
                                                    className="absolute flex flex-col items-center -translate-x-1/2"
                                                    style={{ left: `${position}px` }}
                                                >
                                                    {/* Direction label */}
                                                    <span className={`text-xs xl:text-lg text-shadow-border shadow-black text-white font-bold ${cardinal.label === 'N' ? '!text-red-400 ' : ''} ${cardinal.label === 'S' ? '!text-blue-400 ' : ''}`}>
                                                        {cardinal.label}
                                                    </span>
                                                </div>
                                                {/* 4 tick marks between each cardinal (except after the last one in each set) */}
                                                {index < cardinals.length && (
                                                    <>
                                                        {Array.from({ length: 4 }, (_, tickIndex) => {
                                                            const tickAngle = totalAngle + ((tickIndex + 1) * 45 / 5); // 45 degrees between cardinals, divided by 5 for 4 ticks
                                                            const tickPosition = (tickAngle * 3) + 190;

                                                            return (
                                                                <div
                                                                    key={`tick-${setIndex}-${index}-${tickIndex}`}
                                                                    className="absolute -translate-x-1/2"
                                                                    style={{ left: `${tickPosition}px` }}
                                                                >
                                                                    <div className="w-[1px] h-1 xl:h-3 bg-white/60" />
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default Compass;